import clsx from 'clsx';
import { Skeleton } from './ui/Skeleton.jsx';
import { EmptyState } from './ui/EmptyState.jsx';

/**
 * Lightweight data table that consumes the canonical paginate envelope.
 *
 * Props:
 *  - columns: [{ key, header, render?, className?, headerClassName?, align? }]
 *  - rows: array of records
 *  - loading: bool
 *  - empty: { title, description, action }
 *  - rowKey: (row) => key, default row._id
 *  - onRowClick: optional row activation handler
 */
export function DataTable({ columns, rows = [], loading, empty, rowKey, onRowClick }) {
  const keyFn = rowKey || ((r) => r && r._id);

  if (loading) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-surface-elevated">
        <div className="border-b border-border px-4 py-3">
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="flex flex-col gap-2 p-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <EmptyState
        title={(empty && empty.title) || 'Nessun risultato'}
        description={empty && empty.description}
        action={empty && empty.action}
      />
    );
  }

  return (
    <div className="animate-fade-in overflow-hidden rounded-xl border border-border bg-surface-elevated">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-surface">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-muted',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                    col.headerClassName
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr
                key={keyFn(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={clsx(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-surface-subtle'
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={clsx(
                      'px-4 py-2.5 align-middle text-sm text-ink',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.className
                    )}
                  >
                    {col.render ? col.render(row) : readPath(row, col.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function readPath(obj, path) {
  if (!obj || !path) return '';
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj) ?? '';
}
